import { Auth0ContextInterface, User } from '@auth0/auth0-react'
import { Theme } from '@material-ui/core'
import { NavigateFunction } from 'react-router-dom'
import { UserDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { TelemetryService } from 'services/Telemetry'
import { ShowException } from 'store/Application/appActions'
import { AppDispatch } from 'store/configureStore'
import { assign, createMachine } from 'xstate'
import { AcceptInviteController } from './controllers/AcceptInviteController'
import { UserController } from './controllers/UserController'

type InvitationFlowMachineContext = {
  invitationId?: string
  invitationCode?: string
  tenantId?: string
  auth0?: Auth0ContextInterface<User>
  theme?: Theme
  inviteController?: AcceptInviteController
  organizations?: string[]
  navigate?: NavigateFunction
  dispatch?: AppDispatch
  errors?: Array<{ message: string; status: number }>
  userController?: UserController
  userProfile?: UserDto
  buyerPartyId?: string
}

type InvitationFlowMachineEvents =
  | { type: 'CHANGE_DATA'; data: Partial<InvitationFlowMachineContext> }
  | { type: 'CREATE_USER' }
  | { type: 'USER_CREATED' }
  | { type: 'ACCEPT_INVITE' }
  | { type: 'INVITE_ACCEPTED' }
  | { type: 'INVITE_ERROR'; error: Error }
  | { type: 'CHECK_USER_ACCESS' }
  | { type: 'USER_ACCESS_VALIDATED' }
  | { type: 'CHECK_USER_PARTY_ACCESS' }
  | { type: 'USER_PARTY_ACCESS_VALIDATED' }
  | { type: 'ACCESS_TO_PARTY_DENIED' }
  | { type: 'ERROR'; err: Error }
  | { type: 'SET_USER_CONTROLLER'; userController: UserController }

export const InvitationFlowStateMachine =
  /** @xstate-layout N4IgpgJg5mDOIC5QEkB2A3AlgFwIbcwHtUAxAG0IHcA6TVHTfOqAYgGUBRAFQH1kA5AGrIuAQS7IA8vz4ARANoAGALqJQAB0KwGxNSAAeiAGwB2aiZMBGABwBWW4tsmALM+uKATABoQAT0TWJkbUAJy2Rh4AzM4uHh7WRpYAvkk+aFh4BMTkVLT0BEyorJy8AsJiEtI8AMKSshxKqkggmtpZqHqGCLaR1LbOiol2dh6KFpY+-ggWZiaRlpFjlkYhiXMpaRg4TNkUNHQMhcXcPFwc-KL8pQoqeq06Hc1dJiHUkQ6KnpbOUSGRJrZJsYTNY+oMPEYVstwo4NiB0tt2jl9vlGAQiuwTgBVTgAJUady0D06iBCr0i-2cIWsrlGUUcQO6L2o8UscUhRnein+cIRmSIpD2eUO6OOvC4AAkOABZBq3Zr3dokhAhDzUQLWSxssn-WxRbx+RBuRTURwRMmKEKKazRMK8rb83a5A4FUWY0pCEQcGrSLi4yQAGQDHHx8o0RKVTyNBqmkXc6vmHls1htM2sSftGR2gudqKO7p4uIlHmcPBxIYJCojAuVINNRnClgsjjczkiIUZrOoRnBNmTMQ8lnsmcRAuRwtdzALF2EAHFxHKmuG2jWowhNaFdWaFtEQRNDQgbGq49rLIo2Y4PC4R46cyiRVOSjxZMg2AAFcTVCWV5fEtfpvpwiTOZnGTCkGQPG1qCHCIHABawyR7Zwb2zccXTRKcfxaatdDXRZbHMcE3CvClwnsRl3GCUwXHTel5giFCkSFdD83kSwl2wldcNALp8MIiJiLmd4G0BA8whNFxPlsVZAlAgFGLHZi8zdeQPA4xVVx4xA+JMIj0yEsjRKmRDuwsWwhz1d5vmSVJ4QdVClIfDF5EidScMeLSECTYJ+hcII2zZSlnEZM95j6VV4khbkejZBSnRoABjAAnMAjixWAwCSlhy1xGpcQ4BcbjcriPIMRAXFeVx-i1SISLmCjuW7BIbQcGwVlsOK72oBKAAswASgBrZh0sy0QEoSuBYGyvEeFEapqg4Ng2B4QRRADZBZEKrCNO4srDybXpFjWEJlgQyIG0ZcJehCZxYPCXSgmTTrx16-qhqKEakrGibYCm7b3OVLU5jeQYmxOowzouyDauoUDZKvFMQWpZ6hVewbhoyr7xsmlg2OKv9PKCMwPD+KLITmcnGUhSxuyHc8XB6ICTBR3JcGx9RRT5MAWAgYgwDydBCAG-m2Ymjmuf+krAZmWGqQ5Jt4l06xGSV01rQsUxAisCkWZoUWwA55guZYTKksIJLqHUMh8AAM3NgBbah9fFh0wElgm9sHJxoLJMyvm5IIVepMEk08FZdJujxde6vr0aKb7Jq4Qg31wJLsF8aaQx4D9cS4ABNWb5sW5bVvWzaziKwkpbXZYgjeRZqWTUZQOCg89WCKxWpLBxXBu6O0feqAE9+pOU7TjO5oWpbTkkbPRFzgv6n4ZAOErqtq882vgnAxv00UFvLrrTl-giCOaKjuFUEICA4D0PkHNzJyoCrj2ujPAjoghqxuQ1BYOzEjcNg2QVTCNEeI-cUppUxi-SMnl7CgmkpSfoN1QIlhVvGa0SDqrSRspsLMTFcgDwxqNbGv0YGaT2qqZwCYGbOFOqqak6C1RgQZtgk60dnac1duQ3ab9qSvBJiEEwowKTLHOpEIOCC9StV0uTPU-dY6D2HrAUeqd048NKm-H4BEzw9n+DSd+WpLoDDeAFS0ENP4vGjqbc2GjlRRHCLDX2KYhHAVqhROI0EjqQncFqOwzNbL3wIYlQg9srZgGwJAOxNd+Esl9iI+i4jLoQm7FabkFkggMUCfZYJ0TPIAFojCMkKWrT4ZTymfG+CkFIQA */
  createMachine<InvitationFlowMachineContext, InvitationFlowMachineEvents>(
    {
      predictableActionArguments: true,
      context: {},
      schema: {
        context: {} as InvitationFlowMachineContext,
        events: {} as InvitationFlowMachineEvents,
      },
      id: 'InvitationFlow',
      initial: 'initiating',
      states: {
        initiating: {
          entry: 'trackEvent',
          after: {
            60000: 'error',
          },
          always: [
            {
              target: 'creatingAuth0User',
              cond: 'canCreateAUser',
            },
            {
              target: 'checkingUserAccess',
              cond: 'canCheckUserAccess',
            },
            {
              target: 'acceptingInvite',
              cond: 'canAcceptInvite',
            },
          ],
          on: {
            CHANGE_DATA: {
              actions: 'changeData',
            },
          },
        },
        creatingAuth0User: {
          after: {
            3000: {
              actions: 'redirectToAuth0',
            },
          },
          on: {
            USER_CREATED: {
              target: 'userAuthenticated',
            },
          },
        },
        userAuthenticated: {
          after: {
            3000: 'checkingUserAccess',
          },
        },
        checkingUserAccess: {
          entry: 'trackEvent',
          invoke: {
            src: async (context) => {
              try {
                const userProfile = await context.userController?.GetProfile({
                  disableRedirect: true,
                })

                return userProfile
              } catch (err) {
                ShowException('get user', err)
                throw err
              }
            },
            onDone: [
              {
                target: 'profileReceived',
                actions: 'setUserProfile',
              },
            ],
            onError: [
              {
                target: 'acceptingInvite',
                actions: 'setError',
              },
            ],
          },
        },
        profileReceived: {
          entry: [
            assign((context, ev) => {
              return {
                ...context,
                userProfile: {
                  ...context.userProfile,
                  ...ev['data'],
                },
              }
            }),
            'trackEvent',
          ],
          always: [
            {
              target: 'redirectToFirstParty',
              cond: (context) => context.userProfile?.partiesDto?.length === 1,
            },
            {
              target: 'acceptingInvite',
            },
          ],
        },
        redirectToFirstParty: {
          entry(context) {
            context.userController.SelectWorkspace(
              context.userProfile?.partiesDto[0]?.id
            )

            context.navigate && context.navigate('/app')
          },
        },
        acceptingInvite: {
          entry: 'trackEvent',
          invoke: {
            src: async (context: InvitationFlowMachineContext) => {
              try {
                if (context.invitationCode === '') {
                  throw new Error('Invitation code is empty')
                }
                if (context.invitationId === '') {
                  throw new Error('Invitation id is empty')
                }
                if (context.tenantId === '') {
                  throw new Error('Tenant id is empty')
                }
                if (!context.inviteController) {
                  throw new Error('Invite controller should not be empty')
                }

                return await context.inviteController?.AcceptInvite(
                  context.invitationId,
                  context.invitationCode
                )
              } catch (err) {
                ShowException('accept invite', err)
                throw err
              }
            },
            id: 'acceptInvite',
            onDone: [
              {
                target: 'checkingAccessBuyerToParty',
                actions: assign((_, ev) => {
                  return {
                    buyerPartyId: ev.data.buyerPartyId,
                  }
                }),
              },
            ],
            onError: [
              {
                target: 'error',
                actions: ['setError', 'trackEvent'],
              },
            ],
          },
        },
        checkingAccessBuyerToParty: {
          entry: 'trackEvent',
          on: {
            USER_ACCESS_VALIDATED: {
              target: 'completed',
            },
          },
          invoke: {
            src: async (ctx) => {
              if (ctx.buyerPartyId) {
                // this value should come from accept-invite response
                const userProfile = await ctx.userController.GetProfile({
                  disableRedirect: true,
                })

                if (
                  userProfile.partiesDto.findIndex(
                    (x) => x.id === ctx.buyerPartyId
                  ) > -1
                ) {
                  ctx.userController.SelectWorkspace(ctx.buyerPartyId)
                  return true
                }
              }
              throw new Error('Buyer party id not defined.')
            },
            onDone: [
              {
                target: 'completed',
              },
            ],
            onError: [
              {
                target: 'error',
                actions: ['setError', 'trackEvent'],
              },
            ],
          },
        },
        error: {
          entry: 'setError',
          always: [
            {
              target: 'goToWorkspaces',
              cond: (ctx) => {
                return ctx.userProfile?.partiesDto?.length > 0
              },
            },
          ],
        },
        goToWorkspaces: {
          after: {
            1500: {
              actions: (ctx) => {
                ctx.navigate('/app/workspaces')
              },
            },
          },
        },
        completed: {
          entry: 'trackEvent',
          after: {
            1500: {
              actions: (ctx) => {
                ctx.navigate('/app/projects')
              },
            },
          },
        },
      },
    },
    {
      actions: {
        trackEvent: (context, event) => {
          TelemetryService.getInstance().TrackEvent('InvitationFlow', {
            invitationCode: context.invitationCode,
            invitationId: context.invitationId,
            tenantId: context.tenantId,
            step: event.type,
            message: event['data'] ? JSON.stringify(event['data']) : null,
          })
        },
        changeData: assign((context, ev) => {
          if (ev.type === 'CHANGE_DATA') {
            return {
              ...context,
              ...ev.data,
            }
          }
          return context
        }),
        setUserProfile: assign((context, ev) => {
          return {
            ...context,
            userProfile: ev['data'],
          }
        }),
        setError: assign((context, ev) => {
          const errors: Array<{ message: string; status: number }> = []
          if (context.invitationCode === '') {
            errors.push({ message: 'Invitation code is empty', status: 400 })
          }
          if (context.invitationId === '') {
            errors.push({ message: 'Invitation id is empty', status: 400 })
          }
          if (!context.tenantId || context.tenantId === '') {
            errors.push({ message: 'Tenant id is empty', status: 400 })
          }
          if (ev['data']) {
            errors.push(ev['data'])
          }

          if (errors.length) {
            TelemetryService.getInstance().logError(
              new Error(errors.join(' | '))
            )
          }
          return {
            ...context,
            errors: [...errors],
          }
        }),
        redirectToAuth0: async (context) => {
          const returnUrl = `${location.pathname}?id=${context.invitationId}&code=${context.invitationCode}&tenant=${context.tenantId}`
          await context.auth0?.loginWithRedirect({
            display: 'page',
            prompt: 'login',
            appState: {
              returnUrl: returnUrl,
              invitationId: context.invitationId,
              invitationCode: context.invitationCode,
            },
            screen_hint: 'signUp',
            logo: context.theme.logoSrc,
            primaryColor: context.theme.palette.primary.main,
            page_background: context.theme.palette.background.default,
            backgroundImage: context.theme.backgroundImageSrc,
          })
        },
      },
      guards: {
        canCreateAUser: (context) => {
          return (
            context.tenantId !== undefined &&
            context.auth0?.isLoading === false &&
            context.auth0?.user === undefined &&
            context.invitationId !== '' &&
            context.invitationCode !== '' &&
            context.theme !== undefined
          )
        },
        canCheckUserAccess: (context) => {
          return (
            !context.auth0?.isLoading &&
            context.auth0?.user !== undefined &&
            context.tenantId !== undefined &&
            context.userController !== undefined
          )
        },
        canAcceptInvite: (context) => {
          return (
            context.tenantId !== undefined &&
            context.invitationId !== undefined &&
            context.invitationCode !== undefined &&
            context.auth0?.user !== undefined &&
            context.inviteController !== undefined &&
            context.userController !== undefined
          )
        },
      },
    }
  )
